import type { ReportAiPersonDto } from "./api.ts";
import { aiDollarInput, aiLimitWords, AI_LIMITS_LINE } from "./reportAiBilling.ts";

interface Reply {
  status: number;
  body: (ReportAiPersonDto & { message?: string }) | null;
}

/** A person-specific billing read never paints after its editor, session or target changes. */
export function createReportAiPersonSettings(
  host: HTMLElement,
  options: {
    active: () => boolean;
    read: () => Promise<Reply>;
    save: (input: {
      capDollars: string | null;
      permissionOverride?: boolean | null;
    }) => Promise<Reply>;
    permissionOverride?: boolean;
  },
): { load: () => Promise<void>; reset: () => void } {
  const card = document.createElement("section");
  card.className = "cr-ai-settings-card";
  card.hidden = true;
  host.append(card);
  let generation = 0;
  const current = (expected: number): boolean => expected === generation && options.active();
  const paragraph = (text: string, className?: string): HTMLElement => {
    const result = document.createElement("p");
    result.textContent = text;
    if (className !== undefined) result.className = className;
    return result;
  };
  const money = (cents: number): string =>
    new Intl.NumberFormat("en-US", { style: "currency", currency: "USD" }).format(cents / 100);
  const reset = (): void => {
    generation++;
    card.replaceChildren();
    card.hidden = true;
  };
  function draw(data: ReportAiPersonDto, expected: number): void {
    if (!current(expected)) return;
    card.replaceChildren();
    card.hidden = !data.allowed && options.permissionOverride !== true;
    if (card.hidden) return;
    const title = document.createElement("h3");
    title.textContent = "AI report help";
    card.append(title);
    const status = paragraph("");
    status.setAttribute("role", "status");
    let grant: HTMLSelectElement | undefined;
    if (options.permissionOverride === true) {
      const label = document.createElement("label");
      label.className = "field-label";
      label.textContent = "Use AI report help";
      grant = document.createElement("select");
      grant.className = "field-input";
      grant.disabled = !data.canManage;
      for (const [value, text] of [
        ["inherit", "Use role setting"],
        ["allowed", "Allowed"],
        ["denied", "Not allowed"],
      ]) {
        const option = document.createElement("option");
        option.value = value!;
        option.textContent = text!;
        grant.append(option);
      }
      grant.value =
        data.permissionOverride == null
          ? "inherit"
          : data.permissionOverride
            ? "allowed"
            : "denied";
      label.append(grant);
      card.append(label, paragraph(data.allowed ? "Allowed" : "Not allowed", "admin-field-help"));
    }
    let input: HTMLInputElement | undefined;
    if (data.allowed) {
      const limits = document.createElement("div");
      limits.className = "cr-ai-person-limits";
      const cap = document.createElement("div");
      const spend = document.createElement("div");
      cap.append(paragraph("Monthly limit for this person", "field-label"));
      if (data.canManage) {
        const dollar = aiDollarInput(data.capDollars, "Person monthly limit in dollars");
        input = dollar.input;
        cap.append(
          dollar.field,
          paragraph("Dollars per calendar month. Blank means No limit.", "admin-field-help"),
        );
      } else cap.append(paragraph(aiLimitWords(data.capDollars)));
      spend.append(paragraph(`Month to date (${data.month}): ${money(data.usedCents)}`));
      spend.append(paragraph(`${money(data.reservedCents)} pending`, "admin-field-help"));
      limits.append(cap, spend);
      card.append(limits);
      if (options.permissionOverride !== true)
        card.append(paragraph(AI_LIMITS_LINE, "admin-field-help"));
      if (
        data.capDollars !== null &&
        data.usedCents + data.reservedCents >= Number(data.capDollars) * 100
      )
        card.append(paragraph("Person limit reached", "cr-ai-limit-reached"));
    }
    if (data.canManage && (data.allowed || grant !== undefined)) {
      const save = document.createElement("button");
      save.type = "button";
      save.className = "btn-secondary";
      save.textContent = grant === undefined ? "Save limit" : "Save AI settings";
      save.addEventListener("click", () => {
        if (!current(expected)) return;
        const amount = input?.value.trim();
        if (amount !== undefined && amount !== "" && !/^(0|[1-9][0-9]*)$/.test(amount)) {
          status.textContent = "Enter whole dollars.";
          return;
        }
        const permissionOverride =
          grant === undefined
            ? undefined
            : grant.value === "inherit"
              ? null
              : grant.value === "allowed";
        save.disabled = true;
        if (input !== undefined) input.disabled = true;
        if (grant !== undefined) grant.disabled = true;
        status.textContent = "Saving";
        void options
          .save({
            capDollars: amount === undefined ? data.capDollars : amount === "" ? null : amount,
            ...(permissionOverride === undefined ? {} : { permissionOverride }),
          })
          .then((reply) => {
            if (!current(expected)) return;
            if (reply.status === 401 || reply.status === 403) {
              reset();
              return;
            }
            if (reply.status === 200 && reply.body !== null) {
              draw(reply.body, expected);
            } else status.textContent = reply.body?.message ?? "Could not save AI settings.";
          })
          .catch(() => {
            if (current(expected)) status.textContent = "Could not save AI settings.";
          })
          .finally(() => {
            if (!current(expected)) return;
            save.disabled = false;
            if (input !== undefined) input.disabled = false;
            if (grant !== undefined) grant.disabled = false;
          });
      });
      card.append(save);
    }
    card.append(status);
  }
  async function load(): Promise<void> {
    reset();
    if (!options.active()) return;
    const expected = generation;
    card.hidden = false;
    card.append(paragraph("Loading AI limits"));
    try {
      const reply = await options.read();
      if (!current(expected)) return;
      if (reply.status === 200 && reply.body !== null) draw(reply.body, expected);
      else card.replaceChildren(paragraph(reply.body?.message ?? "AI limits are unavailable."));
    } catch {
      if (current(expected)) card.replaceChildren(paragraph("AI limits are unavailable."));
    }
  }
  return { load, reset };
}
